// 📄 vacacionesService.ts
// Ausencias (vacaciones/permisos/licencias) del usuario — dato propio de la
// PERSONA, no de un proyecto puntual (misma fecha de vacaciones aplica sin
// importar en qué proyecto esté al momento) — vive en
// `usuarios/{uid}/vacaciones/{id}`, mismo namespace que
// usuarios/{uid}/organizaciones. A diferencia del resto de "usuarios/{uid}/
// ..." (100% privado del dueño, ver firestore.rules), esto SÍ es legible
// por cualquier consultor autenticado — es información de baja
// sensibilidad ("estoy fuera estos días"), útil para que compañeros de
// cualquier proyecto compartido puedan planear. Escritura sigue siendo
// exclusiva del dueño (autoservicio, sin aprobación — decisión explícita).
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

export type TipoAusencia = "vacaciones" | "permiso" | "licencia" | "otro";

export const TIPOS_AUSENCIA: { id: TipoAusencia; label: string }[] = [
  { id: "vacaciones", label: "Vacaciones" },
  { id: "permiso", label: "Permiso" },
  { id: "licencia", label: "Licencia" },
  { id: "otro", label: "Otro" }
];

export const tipoAusenciaLabel = (tipo: TipoAusencia): string =>
  TIPOS_AUSENCIA.find((t) => t.id === tipo)?.label || tipo;

export interface VacacionData {
  fechaInicio: string; // YYYY-MM-DD
  fechaFin: string; // YYYY-MM-DD
  tipo: TipoAusencia;
  notas: string;
}

export interface Vacacion extends VacacionData {
  id: string;
  creadoEn?: any;
  actualizadoEn?: any;
}

// Con el uid adjunto — solo lo necesita la vista agregada de equipo (ver
// getVacacionesDeUsuarios), la lectura de "mis propias" no lo necesita
// porque ya está implícito en la ruta que se está consultando.
export interface VacacionDeUsuario extends Vacacion {
  userId: string;
}

const vacacionesRef = (uid: string) => collection(db, "usuarios", uid, "vacaciones");
const vacacionRef = (uid: string, id: string) => doc(db, "usuarios", uid, "vacaciones", id);

export const getVacaciones = async (uid: string): Promise<Vacacion[]> => {
  const snap = await getDocs(vacacionesRef(uid));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Vacacion, "id">) }))
    .sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio));
};

const validar = (data: VacacionData) => {
  if (!data.fechaInicio) throw new Error("La fecha de inicio es obligatoria.");
  if (!data.fechaFin) throw new Error("La fecha de fin es obligatoria.");
  if (data.fechaFin < data.fechaInicio) throw new Error("La fecha de fin no puede ser anterior a la de inicio.");
};

export const crearVacacion = async (uid: string, data: VacacionData): Promise<string> => {
  validar(data);
  const ref = doc(vacacionesRef(uid));
  await setDoc(ref, {
    ...data,
    notas: data.notas.trim(),
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const actualizarVacacion = async (uid: string, id: string, data: VacacionData): Promise<void> => {
  validar(data);
  await updateDoc(vacacionRef(uid, id), {
    ...data,
    notas: data.notas.trim(),
    actualizadoEn: serverTimestamp()
  });
};

export const eliminarVacacion = async (uid: string, id: string): Promise<void> => {
  await deleteDoc(vacacionRef(uid, id));
};

// Para el banner/vista de equipo de un proyecto: trae las ausencias de
// varios usuarios a la vez (un read por uid, en paralelo — los equipos de
// proyecto son chicos, no hace falta una collectionGroup query acá).
export const getVacacionesDeUsuarios = async (uids: string[]): Promise<VacacionDeUsuario[]> => {
  const porUsuario = await Promise.all(
    uids.map(async (uid) => (await getVacaciones(uid)).map((v) => ({ ...v, userId: uid })))
  );
  return porUsuario.flat().sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio));
};

// Fechas LOCALES (no UTC): de noche en América, toISOString ya da el día
// siguiente y una tarea/ausencia se veía vencida o empezada un día antes.
const hoyStr = (): string => new Date().toLocaleDateString('en-CA');
const sumarDias = (fechaStr: string, dias: number): string => {
  const d = new Date(`${fechaStr}T00:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toLocaleDateString('en-CA');
};

// Una ausencia "se cruza" con la ventana [hoy, hoy+dias] si su rango se
// superpone con ese rango — comparación de strings YYYY-MM-DD, válida
// porque el formato ISO ordena lexicográficamente igual que por fecha.
export const seSuperponeConProximosDias = (vacacion: VacacionData, dias = 7): boolean => {
  const desde = hoyStr();
  const hasta = sumarDias(desde, dias);
  return vacacion.fechaFin >= desde && vacacion.fechaInicio <= hasta;
};
